import React, { useState, useRef, ChangeEvent, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Upload,
  AlertCircle,
  Check,
  X,
  Loader,
  Lock,
  ChevronDown
} from 'lucide-react';
import { getUserInfo } from '../../utils/auth';
import { CommonUtils } from '../../utils/commonUtils';
import {
  createProjectFile,
  deleteProjectPrimary,
  existsProjectName,
  getProjectDetail,
  getProjectList,
  parseApiProjectType,
  resolveProjectPrimaryUrl,
  toProjectPrimaryFileRequest,
  updateProject,
  type ProjectTypeCode,
} from '../../utils/project';
import {
  UploadFilePayload,
  resolveImageContentType,
  toUploadFilePayload,
  uploadSingleFileViaPresign,
} from '../../utils/storage';

type ProjectTypeValue = ProjectTypeCode;

const PROJECT_TYPE_OPTIONS = [
  { value: 'STANDALONE' as const, label: '단독' },
  { value: 'PLATFORM' as const, label: '플랫폼' },
  { value: 'BRAND' as const, label: '브랜드' },
] as const;

const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024;
const MIN_LOGO_DIMENSION = 600;
const LOGO_INPUT_ID = 'project-update-logo-input';

// Types
interface ProjectData {
  id: string;
  code: string;
  name: string;
  logo: string | null;  // URL of existing logo
  description: string;
}

interface FormData {
  status: string;
  name: string;
  logo: File | null;
  existingLogo: string | null;
  description: string;
  projectType: ProjectTypeValue;
  parentProjectCode: string;
}

interface ValidationErrors {
  status?: string;
  name?: string;
  logo?: string;
  description?: string;
  projectType?: string;
  parentProjectCode?: string;
}

function parseProjectType(raw: string | undefined): ProjectTypeValue {
  return parseApiProjectType(raw);
}

const ProjectUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode } = useParams<{ projectCode: string }>();

  const [formData, setFormData] = useState<FormData>({
    status: 'ACTIVE',
    name: '',
    logo: null,
    existingLogo: null,
    description: '',
    projectType: 'STANDALONE',
    parentProjectCode: ''
  });

  const [logoPreview, setLogoPreview] = useState<string>('');
  const [uploadFiles, setUploadFiles] = useState<UploadFilePayload[]>([]);
  const [originalHadLogo, setOriginalHadLogo] = useState<boolean>(false);
  const [pendingLogoDelete, setPendingLogoDelete] = useState<boolean>(false);
  const [isLogoValidating, setIsLogoValidating] = useState<boolean>(false);
  const [isLogoUploading, setIsLogoUploading] = useState<boolean>(false);
  const [isNameChecking, setIsNameChecking] = useState<boolean>(false);
  const [isNameVerified, setIsNameVerified] = useState<boolean>(false);
  const [nameCheckMessage, setNameCheckMessage] = useState<string>('');
  const [lastCheckedName, setLastCheckedName] = useState<string>('');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);
  const [hasFileChanges, setHasFileChanges] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [platformProjects, setPlatformProjects] = useState<Array<{ code: string; name: string }>>([]);
  const [originalData, setOriginalData] = useState<FormData>({
    status: 'ACTIVE',
    name: '',
    logo: null,
    existingLogo: null,
    description: '',
    projectType: 'STANDALONE',
    parentProjectCode: ''
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isFetchingRef = useRef<boolean>(false);

  // Fetch project details from API
  const fetchProjectDetails = useCallback(async () => {
    if (!projectCode) {
      setError('프로젝트 코드가 없습니다.');
      setIsLoading(false);
      return;
    }

    // 이미 요청 중이면 중복 호출 방지
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoading(true);
    setError('');
    
    try {
      if (!getUserInfo()) {
        navigate('/login');
        return;
      }

      const projectData = await getProjectDetail(projectCode);
      const existingLogoUrl = await resolveProjectPrimaryUrl(
        projectCode,
        projectData.primaryVersion,
        projectData.updatedAt
      ).catch((err) => {
        console.warn('프로젝트 대표 파일 URL 조회 실패:', projectCode, err);
        return null;
      });

      const newFormData: FormData = {
        status: projectData.status,
        name: projectData.name,
        logo: null,
        existingLogo: existingLogoUrl,
        description: projectData.description,
        projectType: parseProjectType(projectData.projectType),
        parentProjectCode: projectData.parentProjectCode?.trim() ?? ''
      };

      setFormData(newFormData);
      setOriginalData(newFormData);
      setLogoPreview('');
      setOriginalHadLogo(!!existingLogoUrl);
      setPendingLogoDelete(false);
      setUploadFiles([]);
      setIsAdvancedOpen(
        newFormData.projectType === 'BRAND' || newFormData.projectType === 'PLATFORM'
      );
      setIsNameVerified(true);
      setLastCheckedName(projectData.name);
    } catch (error: unknown) {
      console.error('프로젝트 상세 조회 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : CommonUtils.getNetworkErrorMessage(error);
      setError(displayMessage);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [projectCode, navigate]);

  // Load project details on component mount
  useEffect(() => {
    if (projectCode) {
      fetchProjectDetails();
    }
  }, [projectCode, fetchProjectDetails]);

  useEffect(() => {
    const fetchPlatformProjects = async () => {
      try {
        const response = await getProjectList({
          keyword: '',
          status: '',
          type: 'PLATFORM'
        });

        const platforms = (response.projects ?? []).map((project) => ({
          code: project.code,
          name: project.name
        }));

        setPlatformProjects(platforms);

        setFormData((prev) => {
          const hasSelectedParent = platforms.some((platform) => platform.code === prev.parentProjectCode);

          if (prev.projectType === 'BRAND' && platforms.length === 0) {
            return { ...prev, projectType: 'STANDALONE', parentProjectCode: '' };
          }

          if (prev.parentProjectCode && !hasSelectedParent) {
            return { ...prev, parentProjectCode: '' };
          }

          return prev;
        });
      } catch (err) {
        console.error('플랫폼 프로젝트 목록 조회 오류:', err);
        setPlatformProjects([]);
      }
    };

    fetchPlatformProjects();
  }, []);

  // Track changes
  useEffect(() => {
    const formChanged =
      formData.status !== originalData.status ||
      formData.name !== originalData.name ||
      formData.description !== originalData.description ||
      formData.projectType !== originalData.projectType ||
      formData.parentProjectCode !== originalData.parentProjectCode;

    setHasFormChanges(formChanged);
    setHasFileChanges(uploadFiles.length > 0 || pendingLogoDelete);
  }, [formData, originalData, uploadFiles, pendingLogoDelete]);

  const hasChanges = hasFormChanges || hasFileChanges;

  // Handle name input change
  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Limit to 20 characters
    if (value.length > 20) return;
    
    setFormData(prev => ({ ...prev, name: value }));
    
    // Reset verification if name changed from original or last checked
    if (value !== originalData.name && value !== lastCheckedName) {
      setIsNameVerified(false);
      setNameCheckMessage('');
    } else if (value === originalData.name) {
      // If returned to original name, it's automatically verified
      setIsNameVerified(true);
      setNameCheckMessage('');
    }
    
    // Clear error
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: undefined }));
    }
  };

  // Handle description input change
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    // Limit to 100 characters
    if (value.length > 100) return;
    
    setFormData(prev => ({ ...prev, description: value }));
    
    // Clear error
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  // Handle status change
  const handleStatusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, status: value }));
    
    // Clear error
    if (errors.status) {
      setErrors(prev => ({ ...prev, status: undefined }));
    }
  };


  // Check name duplication
  const checkNameDuplication = async () => {
    if (!formData.name.trim()) {
      setErrors(prev => ({ ...prev, name: '프로젝트 이름을 입력해주세요' }));
      return;
    }

    // If name is same as original, no need to check
    if (formData.name === originalData.name) {
      setIsNameVerified(true);
      setNameCheckMessage('현재 프로젝트 이름입니다');
      return;
    }

    setIsNameChecking(true);
    setNameCheckMessage('');

    try {
      const isDuplicate = await existsProjectName(formData.name);

      if (isDuplicate) {
        setIsNameVerified(false);
        setNameCheckMessage('이미 사용 중인 프로젝트 이름입니다');
        setErrors(prev => ({ ...prev, name: '다른 이름을 사용해주세요' }));
      } else {
        setIsNameVerified(true);
        setLastCheckedName(formData.name);
        setNameCheckMessage('사용 가능한 프로젝트 이름입니다');
        setErrors(prev => ({ ...prev, name: undefined }));
      }
    } catch (error) {
      setIsNameVerified(false);
      const serverMessage = error instanceof Error ? error.message : '';
      setNameCheckMessage(serverMessage || '중복 확인 중 오류가 발생했습니다');
      setErrors(prev => ({ ...prev, name: '다시 시도해주세요' }));
    } finally {
      setIsNameChecking(false);
    }
  };

  const resetLogoInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetLogoState = () => {
    setFormData(prev => ({ ...prev, logo: null, existingLogo: null }));
    setLogoPreview('');
    setUploadFiles([]);
    setPendingLogoDelete(false);
    setIsLogoValidating(false);
    setIsLogoUploading(false);
    resetLogoInput();
  };

  const restoreOriginalLogoPreview = () => {
    const originalLogoUrl = originalData.existingLogo;
    setFormData(prev => ({
      ...prev,
      logo: null,
      existingLogo: originalLogoUrl,
    }));
    setLogoPreview('');
    setUploadFiles([]);
    resetLogoInput();
  };

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      console.log('[로고 선택] 파일 없음 (취소)');
      return;
    }

    console.log('[로고 선택] 파일 선택됨', {
      name: file.name,
      size: file.size,
      type: file.type,
    });

    const contentType = resolveImageContentType(file);
    if (!contentType) {
      console.warn('[로고 선택] 허용되지 않은 형식', { fileType: file.type, fileName: file.name });
      setErrors(prev => ({ ...prev, logo: 'PNG, JPG, JPEG, WEBP 파일만 업로드 가능합니다' }));
      resetLogoInput();
      return;
    }

    if (file.size >= MAX_LOGO_SIZE_BYTES) {
      console.warn('[로고 선택] 파일 크기 초과', { size: file.size, max: MAX_LOGO_SIZE_BYTES });
      setErrors(prev => ({ ...prev, logo: '파일 크기는 10MB 미만이어야 합니다' }));
      resetLogoInput();
      return;
    }

    setUploadFiles([]);
    setLogoPreview('');
    setFormData(prev => ({ ...prev, logo: null, existingLogo: null }));

    console.log('[로고 선택] 형식/용량 통과, 해상도 확인 시작', { resolvedContentType: contentType });
    setIsLogoValidating(true);
    setIsLogoUploading(false);
    setErrors(prev => ({ ...prev, logo: undefined }));

    const img = document.createElement('img');
    const reader = new FileReader();
    let isValidated = false;

    const failLogo = (message: string) => {
      console.warn('[로고 선택] 실패', message);
      setErrors(prev => ({ ...prev, logo: message }));
      resetLogoState();
    };

    const uploadLogoToS3 = async (dataUrl: string) => {
      console.log('[로고 선택] 검증 통과, presign → S3 업로드 시작');
      setIsLogoValidating(false);
      setIsLogoUploading(true);

      try {
        const { upload, s3Result } = await uploadSingleFileViaPresign(file, {
          domain: 'PROJECT',
        });

        const uploadFile = toUploadFilePayload(upload);
        console.log('[로고 선택] S3 업로드 완료', { upload, s3Result, uploadFiles: [uploadFile] });

        setFormData(prev => ({ ...prev, logo: file, existingLogo: null }));
        setLogoPreview(dataUrl);
        setUploadFiles([uploadFile]);
        setErrors(prev => ({ ...prev, logo: undefined }));
      } catch (error) {
        const message =
          error instanceof Error ? error.message : '로고 업로드 중 오류가 발생했습니다';
        failLogo(message);
      } finally {
        setIsLogoUploading(false);
      }
    };

    const validateDimensions = (dataUrl: string) => {
      if (isValidated) return;
      isValidated = true;

      console.log('[로고 선택] 이미지 해상도', {
        width: img.naturalWidth,
        height: img.naturalHeight,
        min: MIN_LOGO_DIMENSION,
      });

      if (img.naturalWidth < MIN_LOGO_DIMENSION || img.naturalHeight < MIN_LOGO_DIMENSION) {
        failLogo('이미지 크기는 600x600 픽셀 이상이어야 합니다');
        return;
      }

      void uploadLogoToS3(dataUrl);
    };

    reader.onerror = () => failLogo('이미지를 읽을 수 없습니다');

    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (typeof dataUrl !== 'string') {
        failLogo('이미지를 읽을 수 없습니다');
        return;
      }

      img.onload = () => validateDimensions(dataUrl);
      img.onerror = () => failLogo('이미지를 불러올 수 없습니다');
      img.src = dataUrl;

      if (img.complete && img.naturalWidth > 0) {
        validateDimensions(dataUrl);
      }
    };

    reader.readAsDataURL(file);
  };

  // Remove logo (temp upload cancel or staged delete for saved primary)
  const removeLogo = () => {
    if (uploadFiles.length > 0) {
      if (pendingLogoDelete || !originalHadLogo) {
        setUploadFiles([]);
        setFormData(prev => ({ ...prev, logo: null, existingLogo: null }));
        setLogoPreview('');
      } else {
        restoreOriginalLogoPreview();
      }
      return;
    }

    if (originalHadLogo && !pendingLogoDelete) {
      setPendingLogoDelete(true);
      setFormData(prev => ({ ...prev, logo: null, existingLogo: null }));
      setLogoPreview('');
      resetLogoInput();
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.status.trim()) {
      newErrors.status = '프로젝트 상태를 선택해주세요';
    }

    if (!formData.name.trim()) {
      newErrors.name = '프로젝트 이름을 입력해주세요';
    } else if (!isNameVerified && formData.name !== originalData.name) {
      newErrors.name = '프로젝트 이름 중복 확인이 필요합니다';
    }

    if (formData.projectType === 'BRAND' && !formData.parentProjectCode) {
      newErrors.parentProjectCode = '브랜드 프로젝트는 상위 플랫폼 선택이 필요합니다';
    }

    // Logo is optional for update - no validation needed

    // Description is optional - no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildFormUpdatePayload = () => ({
    name: formData.name,
    description: formData.description,
    status: formData.status,
    type: formData.projectType,
    parentCode:
      formData.projectType === 'BRAND' ? formData.parentProjectCode.trim() : undefined,
  });

  const syncFormStateAfterSave = () => {
    const savedFormData: FormData = {
      status: formData.status,
      name: formData.name,
      logo: null,
      existingLogo: formData.existingLogo,
      description: formData.description,
      projectType: formData.projectType,
      parentProjectCode: formData.parentProjectCode,
    };
    setOriginalData(savedFormData);
    setFormData((prev) => ({
      ...savedFormData,
      logo: prev.logo,
    }));
    setIsNameVerified(true);
    setLastCheckedName(formData.name);
  };

  const applyFileChanges = async (code: string) => {
    const hasNewUpload = uploadFiles.length > 0;
    const shouldDeletePrimary = pendingLogoDelete || (hasNewUpload && originalHadLogo);

    if (shouldDeletePrimary) {
      await deleteProjectPrimary(code);
    }

    for (const uploadFile of uploadFiles) {
      await createProjectFile(code, toProjectPrimaryFileRequest(uploadFile));
    }

    setUploadFiles([]);
    setPendingLogoDelete(false);
    setOriginalHadLogo(hasNewUpload);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isLogoValidating || isLogoUploading) {
      alert('로고 업로드가 진행 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    if (!hasFormChanges && !hasFileChanges) {
      return;
    }

    if (hasFormChanges && !validateForm()) {
      return;
    }

    if (!projectCode) {
      alert('프로젝트 코드가 없습니다.');
      return;
    }

    setIsSubmitting(true);

    let formSaved = false;

    try {
      if (hasFormChanges) {
        await updateProject(projectCode, buildFormUpdatePayload());
        syncFormStateAfterSave();
        formSaved = true;
      }

      if (hasFileChanges) {
        try {
          await applyFileChanges(projectCode);
        } catch (fileError) {
          if (formSaved) {
            alert(
              '프로젝트 정보는 저장되었습니다.\n로고 파일 처리에 실패했습니다. 다시 시도해 주세요.'
            );
            return;
          }
          throw fileError;
        }
      }

      alert('프로젝트가 성공적으로 수정되었습니다!');
      navigate('/projects');
    } catch (error: unknown) {
      console.error('프로젝트 수정 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : CommonUtils.getNetworkErrorMessage(error) || '프로젝트 수정 중 오류가 발생했습니다.';
      alert(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('수정사항이 저장되지 않습니다. 취소하시겠습니까?')) {
        navigate(-1);
      }
    } else {
        navigate(-1);
    }
  };

  // 목록과 동일: resolveProjectPrimaryUrl 결과 또는 새 업로드 미리보기만 사용
  const logoUrl =
    pendingLogoDelete && uploadFiles.length === 0
      ? null
      : logoPreview || formData.existingLogo || null;

  const handleLogoImageError = () => {
    if (logoPreview) {
      setLogoPreview('');
      setUploadFiles([]);
      setFormData(prev => ({ ...prev, logo: null }));
      return;
    }

    setFormData(prev => ({ ...prev, existingLogo: null }));
    setOriginalHadLogo(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={handleCancel}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">프로젝트 수정</h1>
            </div>
            {hasChanges && (
              <span className="text-sm text-orange-600 flex items-center">
                <AlertCircle className="w-4 h-4 mr-1" />
                수정사항이 있습니다
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 text-gray-400 mx-auto mb-4 animate-spin" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">프로젝트 정보를 불러오는 중...</h3>
            <p className="text-gray-500">잠시만 기다려주세요.</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-12">
            <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">프로젝트 정보를 불러올 수 없습니다</h3>
            <p className="text-gray-500 mb-4 whitespace-pre-line text-center max-w-md mx-auto">{error}</p>
            <button 
              onClick={fetchProjectDetails}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              다시 시도
            </button>
          </div>
        )}

        {/* Form Content */}
        {!isLoading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 sm:p-8">
            {/* Instructions */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">프로젝트 정보 수정</h2>
              <p className="text-sm text-gray-600">
                프로젝트의 정보를 수정할 수 있습니다. 프로젝트 코드는 변경할 수 없습니다.
              </p>
            </div>

            <div className="space-y-6">
              {/* Project Code (Read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 코드
                </label>
                <div className="flex items-center">
                  <Lock className="w-5 h-5 text-gray-400 mr-2" />
                  <input
                    type="text"
                    value={projectCode || ''}
                    disabled
                    className="flex-1 px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">프로젝트 코드는 변경할 수 없습니다</p>
              </div>

              {/* Project Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 상태 <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={handleStatusChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                    errors.status ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="ACTIVE">활성</option>
                  <option value="INACTIVE">비활성</option>
                  <option value="PENDING">대기중</option>
                  <option value="ARCHIVED">보관됨</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-500 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.status}
                  </p>
                )}
                <div className="mt-2 flex items-center space-x-3">
                  <div className="flex items-center">
                    <span className={`w-2 h-2 rounded-full mr-2 ${
                      formData.status === 'ACTIVE' ? 'bg-green-500' :
                      formData.status === 'INACTIVE' ? 'bg-gray-400' :
                      formData.status === 'PENDING' ? 'bg-yellow-400' :
                      'bg-red-400'
                    }`} />
                    <span className="text-sm text-gray-600">
                      {formData.status === 'ACTIVE' ? '프로젝트가 정상 운영 중입니다' :
                       formData.status === 'INACTIVE' ? '프로젝트가 일시 중지되었습니다' :
                       formData.status === 'PENDING' ? '프로젝트가 준비 중입니다' :
                       '프로젝트가 보관되었습니다'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Project Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 이름 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleNameChange}
                      placeholder="프로젝트 이름 입력 (최대 20자)"
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      maxLength={20}
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <div>
                        {errors.name && (
                          <p className="text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.name}
                          </p>
                        )}
                        {nameCheckMessage && (
                          <p className={`text-sm flex items-center ${
                            isNameVerified ? 'text-green-600' : 'text-red-500'
                          }`}>
                            {isNameVerified ? (
                              <Check className="w-4 h-4 mr-1" />
                            ) : (
                              <X className="w-4 h-4 mr-1" />
                            )}
                            {nameCheckMessage}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formData.name.length}/20
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={checkNameDuplication}
                    disabled={
                      isNameChecking || 
                      (isNameVerified && formData.name === lastCheckedName) || 
                      !formData.name.trim() ||
                      formData.name === originalData.name
                    }
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                      isNameVerified && (formData.name === lastCheckedName || formData.name === originalData.name)
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : isNameChecking || !formData.name.trim()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {isNameChecking ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        확인 중...
                      </>
                    ) : isNameVerified ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        확인 완료
                      </>
                    ) : (
                      '중복 확인'
                    )}
                  </button>
                </div>
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  로고 이미지
                </label>
                <div className="text-xs text-gray-500 mb-3">
                  PNG, JPG, JPEG, WEBP 파일만 업로드 가능 (최소 600x600 픽셀, 10MB 미만)
                </div>
                
                {!logoUrl ? (
                  <label
                    htmlFor={LOGO_INPUT_ID}
                    className={`block border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      errors.logo
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
                    } ${isLogoValidating || isLogoUploading ? 'pointer-events-none opacity-70' : ''}`}
                  >
                    {isLogoUploading ? (
                      <>
                        <Loader className="w-12 h-12 text-gray-400 mx-auto mb-3 animate-spin" />
                        <p className="text-sm text-gray-600 mb-1">S3 업로드 중...</p>
                      </>
                    ) : isLogoValidating ? (
                      <>
                        <Loader className="w-12 h-12 text-gray-400 mx-auto mb-3 animate-spin" />
                        <p className="text-sm text-gray-600 mb-1">이미지 확인 중...</p>
                      </>
                    ) : (
                      <>
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-1">
                          클릭하여 이미지를 업로드하세요
                        </p>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, JPEG, WEBP • 600x600px 이상 • 10MB 미만
                        </p>
                      </>
                    )}
                  </label>
                ) : (
                  // Display existing or new logo
                  <div className="flex items-start space-x-4">
                    <div className="relative w-32 h-32 flex-shrink-0">
                      <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                        <img
                          src={logoUrl}
                          alt="Logo preview"
                          className="w-full h-full object-cover"
                          onError={handleLogoImageError}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 mb-1">
                        {formData.logo ? formData.logo.name : '기존 로고'}
                      </p>
                      <p className="text-xs text-gray-500 mb-3">
                        {formData.logo
                          ? `${Math.round(formData.logo.size / 1024)} KB`
                          : '저장된 이미지'}
                        {uploadFiles.length > 0 && ' • S3 업로드 완료'}
                      </p>
                      <label
                        htmlFor={LOGO_INPUT_ID}
                        className={`text-sm text-blue-600 hover:underline cursor-pointer ${
                          isLogoValidating || isLogoUploading ? 'pointer-events-none opacity-50' : ''
                        }`}
                      >
                        다른 이미지 선택
                      </label>
                    </div>
                  </div>
                )}
                
                {errors.logo && (
                  <p className="mt-2 text-sm text-red-500 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {errors.logo}
                  </p>
                )}
                
                <input
                  id={LOGO_INPUT_ID}
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 설명
                </label>
                <textarea
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="프로젝트에 대한 간단한 설명을 입력하세요 (최대 100자)"
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  maxLength={100}
                />
                <div className="mt-1 flex items-center justify-between">
                  {errors.description && (
                    <p className="text-sm text-red-500 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description}
                    </p>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {formData.description.length}/100
                  </span>
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setIsAdvancedOpen((prev) => !prev)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">고급 설정 (선택)</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      프로젝트 타입과 상위 플랫폼을 지정할 수 있습니다.
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isAdvancedOpen && (
                  <div className="p-4 space-y-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        프로젝트 타입 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {PROJECT_TYPE_OPTIONS.map((option) => {
                          const isBrandWithoutPlatforms = option.value === 'BRAND' && platformProjects.length === 0;
                          const isSelected = formData.projectType === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                if (isBrandWithoutPlatforms) return;
                                setFormData((prev) => ({
                                  ...prev,
                                  projectType: option.value,
                                  parentProjectCode: option.value === 'BRAND' ? prev.parentProjectCode : ''
                                }));
                                setErrors((prev) => ({
                                  ...prev,
                                  projectType: undefined,
                                  parentProjectCode: option.value === 'BRAND' ? prev.parentProjectCode : undefined
                                }));
                              }}
                              disabled={isBrandWithoutPlatforms}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                isSelected
                                  ? 'bg-gray-900 text-white border-gray-900'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              } ${isBrandWithoutPlatforms ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      {platformProjects.length === 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          선택 가능한 플랫폼 프로젝트가 없어 현재는 단독/플랫폼 타입만 선택할 수 있습니다.
                        </p>
                      )}
                    </div>

                    {formData.projectType === 'BRAND' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          상위 플랫폼 <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.parentProjectCode}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, parentProjectCode: value }));
                            if (errors.parentProjectCode && value) {
                              setErrors((prev) => ({ ...prev, parentProjectCode: undefined }));
                            }
                          }}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                            errors.parentProjectCode ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">상위 플랫폼을 선택하세요</option>
                          {platformProjects.map((project) => (
                            <option key={project.code} value={project.code}>
                              {project.name} ({project.code})
                            </option>
                          ))}
                        </select>
                        {errors.parentProjectCode && (
                          <p className="mt-2 text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.parentProjectCode}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Form Preview */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">미리보기</h3>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt="Preview"
                        className="w-16 h-16 object-cover rounded-lg"
                        onError={handleLogoImageError}
                      />
                    ) : (
                      <div className="text-4xl">📁</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500">{projectCode}</p>
                    <h4 className="font-semibold text-gray-900">
                      {formData.name || '프로젝트 이름'}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.description || '프로젝트 설명이 여기에 표시됩니다'}
                    </p>
                    <div className="mt-2">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        formData.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                        formData.status === 'INACTIVE' ? 'bg-gray-100 text-gray-800' :
                        formData.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {formData.status === 'ACTIVE' ? '활성' :
                         formData.status === 'INACTIVE' ? '비활성' :
                         formData.status === 'PENDING' ? '대기중' :
                         '보관됨'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || (!hasFormChanges && !hasFileChanges) || isLogoValidating || isLogoUploading}
                className={`px-6 py-2 bg-gray-900 text-white rounded-lg transition-colors flex items-center ${
                  isSubmitting || (!hasFormChanges && !hasFileChanges) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    수정 중...
                  </>
                ) : (
                  '프로젝트 수정'
                )}
              </button>
            </div>

            {/* Help Text */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>도움말:</strong> 프로젝트 이름을 변경할 경우 반드시 중복 확인을 해주세요.
                로고 이미지는 정사각형 비율을 권장합니다.
              </p>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ProjectUpdate;